/**
 * ErrorBoundary — generic React error boundary. Wrap any subtree that
 * might throw at render time so the rest of the page stays usable AND
 * the operator sees a helpful message instead of a blank screen.
 *
 * Usage:
 *   <ErrorBoundary name="CRM Agreements tab">
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Optional props:
 *   name      — short label shown in the error panel (helps support)
 *   onReset   — called when the user clicks "Try again"; you can reset
 *               whatever parent state may have caused the crash
 */
import React from 'react';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error in the browser console so support can grab it
    // without needing the React DevTools. Production users won't see this.
    console.error(`[ErrorBoundary${this.props.name ? `:${this.props.name}` : ''}]`, error, info);
    this.setState({ info });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, info: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || String(this.state.error || 'Unknown error');
    const stack = (this.state.error?.stack || '').split('\n').slice(0, 6).join('\n');

    return (
      <div style={{
        margin: 16, padding: 16,
        background: 'rgba(220, 38, 38, 0.06)',
        border: '1px solid rgba(220, 38, 38, 0.3)',
        borderRadius: 10, color: 'var(--money-due, #7f1d1d)',
      }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>
          Something broke {this.props.name ? `in ${this.props.name}` : 'here'}.
        </div>
        <div style={{ fontSize: 13, marginBottom: 8 }}>{msg}</div>
        <details style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>
          <summary style={{ cursor: 'pointer' }}>Stack trace (for support)</summary>
          <pre style={{ whiteSpace: 'pre-wrap', marginTop: 6, fontSize: 11, background: 'rgba(0,0,0,0.04)', padding: 8, borderRadius: 6 }}>{stack}</pre>
        </details>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={this.reset} style={{
            padding: '6px 14px', borderRadius: 6, border: 'none',
            background: '#dc2626', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>
            Try again
          </button>
          {/* "Try again" re-renders the same state and can fail straight back
              into this panel. A reload is the thing the operator reached for
              on their own, so it is offered rather than left to be guessed. */}
          <button onClick={() => window.location.reload()} style={{
            padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', background: 'transparent',
            color: 'var(--text-primary)',
            border: '1px solid var(--divider, rgba(148,163,184,0.4))',
          }}>
            Reload the page
          </button>
        </div>
      </div>
    );
  }
}
